import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText,
  Loader2,
  X,
  Square,
  ArrowUp,
  ChevronDown,
  ChevronsDown,
} from "lucide-react";

import { ALLOWED_EXTENSIONS } from "../utils/fileHelpers";

import TiptapInput, { getEditorMarkdown } from "./TiptapInput";
import RichTextToolbar from "./RichTextToolbar";
import EmojiPicker from "./EmojiPicker";
import DropDownPromptLoader from "./DropDownPromptLoader";
import DropDownNotes from "./DropDownNotes";
import DropDownModelSelector from "./DropDownModelSelector";
import DropDownAgentSelector from "./DropDownAgentSelector";
import TokenCounter from "./TokenCounter";
import MentionDropdown from "./MentionDropdown";
import { agents } from "../utils/constants";

/* ─── Dropdown key registry ─── */
const DROPDOWN_KEYS = ["agent", "prompt", "notes", "emoji", "model"];
const EMPTY_DROPDOWNS = Object.fromEntries(
  DROPDOWN_KEYS.map((k) => [k, false]),
);

/* Custom Lucide-style icon: triple-down chevron.
   Lucide doesn't ship a 3-chevron icon, so this is hand-rolled but mirrors
   their design grammar exactly:
     - 24×24 viewBox
     - fill="none", stroke="currentColor"
     - round line caps + joins
   Chevron y-positions are 3 / 10 / 17 — same 7-unit interval Lucide uses
   between ChevronsDown's two chevrons. */
const ChevronsTripleDown = ({ className = "", strokeWidth = 2, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="m7 3 5 5 5-5" />
    <path d="m7 10 5 5 5-5" />
    <path d="m7 17 5 5 5-5" />
  </svg>
);

/* ─── Small presentational helpers ─── */

const ToolbarDivider = () => (
  <div className="w-px h-4 bg-dropdownDivider mx-1.5" aria-hidden />
);

const AttachmentChip = ({ file, onRemove }) => {
  const statusClasses =
    file.status === "error"
      ? "border-logoutColor/40 bg-logoutColor/10 text-logoutColor"
      : "border-inputBorder bg-inputBg/20 text-mutedText hover:bg-inputBg/40";

  const StatusIcon =
    file.status === "uploading"
      ? Loader2
      : file.status === "error"
        ? X
        : FileText;

  return (
    <div
      className={[
        "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1",
        "text-[12px] font-normal tracking-wide transition-colors",
        statusClasses,
      ].join(" ")}
    >
      <StatusIcon
        className={[
          "h-3 w-3 shrink-0",
          file.status === "uploading" && "animate-spin",
        ]
          .filter(Boolean)
          .join(" ")}
      />
      <span className="truncate max-w-[150px]">
        {file.status === "error" ? file.error : file.name}
      </span>
      <button
        onClick={() => onRemove(file.id)}
        className="ml-0.5 rounded p-0.5 hover:bg-btnHover transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
        aria-label="Remove file"
      >
        <X className="h-3 w-3" strokeWidth={2} />
      </button>
    </div>
  );
};

const ThreadLockedOverlay = ({ name, onUnlock }) => {
  const initials = (name || "?").slice(0, 2).toUpperCase();
  return (
    <div
      className="absolute inset-0 z-[70] flex items-center justify-between gap-3 px-5
                 rounded-2xl bg-surface/95 backdrop-blur-md
                 border border-velBlue/30 animate-nav-pulse"
      role="alert"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-6 h-6 rounded-full bg-velBlue/20 flex items-center justify-center shrink-0">
          <span className="text-[10px] font-medium text-velBlue tracking-wide">
            {initials}
          </span>
        </div>
        <span className="text-[13px] font-normal tracking-wide text-mutedText truncate">
          {name || "Someone"} is getting a response…
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUnlock?.();
        }}
        className="shrink-0 text-[12px] font-normal tracking-wide
                   text-mutedText hover:text-mainText
                   transition-colors px-2.5 py-1 rounded-lg hover:bg-btnHover
                   outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
      >
        Unlock
      </button>
    </div>
  );
};

const StopButton = ({ onStop }) => (
  <button
    aria-label="Stop generating"
    onClick={onStop}
    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1
               text-[12px] font-normal tracking-wide text-logoutColor
               hover:bg-logoutColor/10 transition-colors cursor-pointer
               outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
  >
    <Square className="h-3 w-3" fill="currentColor" strokeWidth={0} />
    <span>Stop</span>
  </button>
);

// Quick    — single down chevron        (shallow)
// Standard — double down chevrons       (going deeper)
// Deep     — triple down chevrons       (deepest)
const THINKING_MODES = [
  { id: "quick", label: "Quick", Icon: ChevronDown },
  { id: "standard", label: "Standard", Icon: ChevronsDown },
  { id: "deep", label: "Deep", Icon: ChevronsTripleDown },
];

const ThinkingModeToggle = ({ value, onChange }) => (
  <div
    className="inline-flex items-center gap-0.5"
    role="radiogroup"
    aria-label="Discovery research depth"
  >
    {THINKING_MODES.map((m) => {
      const active = value === m.id;
      const { Icon } = m;
      return (
        <button
          key={m.id}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange?.(m.id)}
          className={[
            "inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] font-normal tracking-wide transition-colors cursor-pointer",
            "outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
            active
              ? "bg-velBlue/15 text-velBlue"
              : "text-mutedText hover:text-mainText",
          ].join(" ")}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span>{m.label}</span>
        </button>
      );
    })}
  </div>
);

const SendButton = ({ disabled, onSend }) => (
  <button
    aria-label="Send message"
    onClick={onSend}
    disabled={disabled}
    className={[
      "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1",
      "text-[12px] font-normal tracking-wide transition-colors",
      "outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
      disabled
        ? "text-mutedText/50 cursor-not-allowed"
        : "text-velBlue hover:bg-velBlue/10 cursor-pointer",
    ].join(" ")}
  >
    <ArrowUp className="h-3 w-3" strokeWidth={2.25} />
    <span>Send</span>
  </button>
);

/* ─── ChatEntry ─── */

const ChatEntry = (props) => {
  const [openDropdowns, setOpenDropdowns] = useState(EMPTY_DROPDOWNS);
  const [mentionedUuids, setMentionedUuids] = useState([]);
  const editorRef = useRef(null);
  const chatTypingTimeoutRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const setDropdown = useCallback((key, value) => {
    setOpenDropdowns((prev) => ({ ...prev, [key]: value }));
  }, []);

  const closeAllExcept = useCallback((keepKey) => {
    setOpenDropdowns((prev) =>
      Object.keys(prev).reduce(
        (acc, k) => ({ ...acc, [k]: k === keepKey ? prev[k] : false }),
        {},
      ),
    );
  }, []);

  const closeAllDropdowns = useCallback(
    () => setOpenDropdowns(EMPTY_DROPDOWNS),
    [],
  );

  const makeSetter = useCallback(
    (key) => (value) => setDropdown(key, value),
    [setDropdown],
  );

  /* Prefill from ?message= */
  const prefillHandled = useRef(false);
  useEffect(() => {
    if (prefillHandled.current) return;
    const prefill = searchParams.get("message");
    if (!prefill) return;

    props.setChatEntry(prefill);

    const hydrateEditor = () => {
      const ed = editorRef.current?.editor;
      if (ed) {
        ed.commands.setContent(prefill);
        ed.commands.focus("end");
      } else {
        requestAnimationFrame(hydrateEditor);
      }
    };
    hydrateEditor();

    searchParams.delete("message");
    setSearchParams(searchParams, { replace: true });
    prefillHandled.current = true;
  }, [searchParams, setSearchParams, props.setChatEntry]);

  const handleMention = (uuid) =>
    setMentionedUuids((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));

  const handleEmojiSelect = (emoji) => {
    const ed = editorRef.current?.editor;
    if (ed) ed.chain().focus().insertContent(emoji).run();
    else props.setChatEntry(props.chatEntry + emoji);
  };

  const blockDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files?.length > 0) props.onFilesSelected(files);
    e.target.value = "";
  };

  const handleSend = (...args) => {
    if (props.chatType === "team") {
      const [msg] = args;
      props.onTeamSend(msg, mentionedUuids);
      setMentionedUuids([]);
      props.setChatEntry("");
      editorRef.current?.editor?.commands.clearContent();
      return;
    }
    props.onChatStopTyping?.();
    clearTimeout(chatTypingTimeoutRef.current);
    props.sendPayload(...args);
  };

  const handleManualSend = () => {
    const ed = editorRef.current?.editor;
    const md = ed ? getEditorMarkdown(ed) : "";
    if (!md.trim()) return;
    handleSend(md, props.selectedAgent?.apiName, props.currentThreadId);
    if (props.chatType !== "team" && ed) {
      requestAnimationFrame(() => ed.commands.clearContent());
    }
  };

  const handleInputChange = (() => {
    if (props.chatType === "team") return props.onTeamInputChange;
    if (!props.onChatTyping) return undefined;
    return () => {
      props.onChatTyping();
      clearTimeout(chatTypingTimeoutRef.current);
      chatTypingTimeoutRef.current = setTimeout(() => {
        props.onChatStopTyping?.();
      }, 2000);
    };
  })();

  const buildToolbarItems = () => {
    const items = [];

    if (props.chatType === "public") {
      items.push(
        {
          key: "model",
          node: (
            <DropDownModelSelector
              models={props.publicModels || []}
              selectedModel={props.selectedModel || ""}
              onSelect={props.onSelectModel}
              isOpen={openDropdowns.model}
              onToggle={() => setDropdown("model", !openDropdowns.model)}
              closeOthers={() => closeAllExcept("model")}
              favorites={props.favoriteModels || []}
              onToggleFavorite={props.onToggleFavorite}
            />
          ),
        },
        {
          key: "token",
          node: (
            <TokenCounter
              systemPrompt={props.systemPrompt || ""}
              currentMessage={props.chatEntry || ""}
              messages={props.messages || []}
              contextWindow={props.contextWindow || 128000}
              historyDepth={props.historyDepth ?? 5}
              onChangeDepth={props.onChangeDepth}
            />
          ),
        },
      );
    } else if (props.chatType === "private") {
      items.push({
        key: "agent",
        node: (
          <DropDownAgentSelector
            agents={agents}
            selectedAgent={props.selectedAgent}
            setSelectedAgent={props.setSelectedAgent}
            isAgentSelectorOpen={openDropdowns.agent}
            setIsAgentSelectorOpen={makeSetter("agent")}
            setIsDropDownNotesOpen={makeSetter("notes")}
            setIsPromptLoaderOpen={makeSetter("prompt")}
          />
        ),
      });

      if (props.selectedAgent?.apiName === "DISCOVERY") {
        items.push({
          key: "thinking",
          node: (
            <ThinkingModeToggle
              value={props.thinkingMode}
              onChange={props.setThinkingMode}
            />
          ),
        });
      }

      items.push(
        {
          key: "prompt",
          node: (
            <DropDownPromptLoader
              chatEntry={props.chatEntry}
              setChatEntry={props.setChatEntry}
              isAgentSelectorOpen={openDropdowns.agent}
              setIsAgentSelectorOpen={makeSetter("agent")}
              isPromptLoaderOpen={openDropdowns.prompt}
              setIsPromptLoaderOpen={makeSetter("prompt")}
              isDropDownNotesOpen={openDropdowns.notes}
              setIsDropDownNotesOpen={makeSetter("notes")}
            />
          ),
        },
        {
          key: "notes",
          node: (
            <DropDownNotes
              isDropDownNotesOpen={openDropdowns.notes}
              setIsDropDownNotesOpen={makeSetter("notes")}
              setIsAgentSelectorOpen={makeSetter("agent")}
              setIsPromptLoaderOpen={makeSetter("prompt")}
            />
          ),
        },
      );
    }

    items.push(
      {
        key: "rich",
        node: <RichTextToolbar editor={editorRef.current?.editor} />,
      },
      {
        key: "emoji",
        node: (
          <EmojiPicker
            isOpen={openDropdowns.emoji}
            setIsOpen={makeSetter("emoji")}
            onSelect={handleEmojiSelect}
            closeOthers={() => closeAllExcept("emoji")}
          />
        ),
      },
    );

    return items;
  };

  const toolbarItems = buildToolbarItems();
  const sendDisabled = !(props.chatEntry || "").trim();

  return (
    <div
      className="w-full font-poppins"
      onDragEnter={blockDrop}
      onDragLeave={blockDrop}
      onDragOver={blockDrop}
      onDrop={blockDrop}
    >
      {!props.compact && (
        <input
          ref={props.fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={[...ALLOWED_EXTENSIONS].map((ext) => `.${ext}`).join(",")}
          onChange={handleFileInputChange}
        />
      )}

      <div
        className={props.compact ? "w-full" : "w-full max-w-5xl px-6 pt-2 pb-4"}
      >
        <div
          className={[
            "relative w-full",
            props.compact
              ? "bg-surface/90 border-t border-surfaceBorder"
              : [
                  "rounded-2xl bg-surface/80 backdrop-blur-md",
                  "border border-surfaceBorder",
                  "shadow-[0_4px_24px_rgba(0,0,0,0.08)]",
                  "dark:shadow-[0_4px_24px_rgba(0,0,0,0.45)]",
                ].join(" "),
          ].join(" ")}
        >
          {!props.compact && (
            <div className="flex items-center px-3 pt-2.5 pb-1.5">
              {toolbarItems.map((it, i) => (
                <Fragment key={it.key}>
                  {i > 0 && <ToolbarDivider />}
                  {it.node}
                </Fragment>
              ))}
            </div>
          )}

          {!props.compact && props.attachedFiles?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pb-1.5">
              {props.attachedFiles.map((af) => (
                <AttachmentChip
                  key={af.id}
                  file={af}
                  onRemove={props.onRemoveFile}
                />
              ))}
            </div>
          )}

          {!props.compact && <div className="h-px bg-dropdownDivider mx-3" />}

          <div
            className={
              props.compact
                ? "relative px-2 pt-1.5 pb-4"
                : "relative px-4 pt-3 pb-3"
            }
            onClick={closeAllDropdowns}
          >
            {!props.compact && props.threadLocked && (
              <ThreadLockedOverlay
                name={props.threadLockedByName}
                onUnlock={props.onUnlockThread}
              />
            )}

            {props.chatType === "team" && (
              <MentionDropdown
                editorRef={editorRef}
                teamId={props.currentTeamId}
                onMention={handleMention}
              />
            )}

            <TiptapInput
              chatEntry={props.chatEntry}
              setChatEntry={props.setChatEntry}
              selectedAgent={props.selectedAgent}
              sendPayload={handleSend}
              currentThreadId={props.currentThreadId}
              sentHistory={props.sentHistory}
              editorRef={editorRef}
              onInputChange={handleInputChange}
              isLoadingThread={props.isLoadingThread}
            />
          </div>

          <div className="flex items-center justify-end px-3 pb-2 pt-1">
            {props.isStreaming ? (
              <StopButton onStop={props.onStop} />
            ) : (
              <SendButton disabled={sendDisabled} onSend={handleManualSend} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatEntry;
